import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Clock, Sparkles, Check } from "lucide-react";
import Logo from "@/components/Logo";
import EventDemoScope from "@/components/EventDemoScope";
import SpeakersGrid from "@/components/SpeakersGrid";

function EventLogo({ event }) {
  const src = String(event?.logo || "").trim();
  if (src) {
    return <img src={src} alt={event?.title || "Event"} className="h-8 w-auto max-w-[160px] object-contain" />;
  }
  return <Logo />;
}

function formatDateRange(startDate, endDate) {
  if (!startDate) return "Dates announced soon";
  const start = new Date(startDate + "T00:00:00");
  const end = endDate ? new Date(endDate + "T00:00:00") : null;
  const fmt = (d) =>
    d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  if (end && end > start) return `${fmt(start)} – ${fmt(end)}`;
  return fmt(start);
}

export default function DemoEventLanding({ bundle, registerPath }) {
  const event = bundle?.event || {};
  const page = bundle?.page || {};
  const hero = page.hero || {};
  const pillars = page.pillars || {};
  const venue = page.venue || {};
  const cta = page.cta || {};
  const sessions = bundle?.sessions || [];
  const speakers = bundle?.speakers || [];
  const sponsors = bundle?.sponsors || [];
  const accent = event.accent || "#D9F25E";
  const titleMain = event.title || "Stride";
  const titleYear = event.titleAccent || event.startDate?.slice(0, 4) || "2026";

  return (
    <EventDemoScope>
      <div className="min-h-screen grain">
        <header className="demo-nav-blur fixed top-0 inset-x-0 z-40 border-b border-[var(--border-default)]">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8 h-[4.25rem] flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <EventLogo event={event} />
              <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                Global demo
              </span>
            </div>
            <nav className="hidden md:flex items-center gap-6 text-sm text-[var(--text-secondary)]">
              <a href="#schedule" className="hover:text-[var(--text-primary)]">
                Schedule
              </a>
              <a href="#speakers" className="hover:text-[var(--text-primary)]">
                Speakers
              </a>
              <a href="#venue" className="hover:text-[var(--text-primary)]">
                Venue
              </a>
            </nav>
            <Link to={registerPath} className="btn-primary text-sm" data-testid="public-register-cta">
              Register <ArrowRight size={14} />
            </Link>
          </div>
        </header>

        <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
          <div className="absolute inset-0 dot-grid opacity-20 pointer-events-none" />
          <div className="absolute -top-24 right-[-5%] w-[min(640px,85vw)] h-[min(640px,85vw)] demo-hero-glow rounded-full pointer-events-none" />
          {event.cover ? (
            <div className="absolute inset-0 pointer-events-none">
              <img src={event.cover} alt="" className="w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-base)] via-[var(--bg-base)]/85 to-[var(--bg-base)]/40" />
            </div>
          ) : null}
          <div className="max-w-[1320px] mx-auto px-4 md:px-8 relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--border-default)] text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--text-secondary)] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)] pulse-dot" />
              {hero.eyebrow || "Live demo"}
            </div>
            <h1 className="font-heading text-[clamp(2.5rem,9vw,6.5rem)] leading-[0.92] tracking-[-0.04em] font-semibold">
              {titleMain}{" "}
              <em className="not-italic" style={{ color: accent }}>
                / {titleYear}
              </em>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[var(--text-secondary)] max-w-2xl leading-relaxed">
              {hero.subtitle || event.subtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to={registerPath} className="btn-primary" data-testid="hero-register">
                {cta.label || "Register"} <ArrowRight size={14} />
              </Link>
              <a href="#schedule" className="btn-secondary">
                View schedule
              </a>
            </div>
            {cta.hint ? <p className="mt-3 text-xs text-[var(--text-tertiary)]">{cta.hint}</p> : null}

            <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
              {[
                [Calendar, "Dates", formatDateRange(event.startDate, event.endDate)],
                [MapPin, "Venue", event.venue],
                [Clock, "Timezone", (event.timezone || "UTC").split("/").pop()?.replace("_", " ") || "UTC"],
                [Sparkles, "Format", event.format || "In-person"],
              ].map(([Icon, label, value]) => (
                <div key={label} className="bg-[var(--bg-surface)] p-5">
                  <Icon size={14} className="text-[var(--primary)] mb-2" />
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{label}</div>
                  <div className="font-mono text-sm mt-2 whitespace-pre-line leading-relaxed">{value}</div>
                </div>
              ))}
            </div>

            {(hero.stats || []).length > 0 ? (
              <div className="mt-px grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-t-0 border-[var(--border-default)]">
                {hero.stats.map((s) => (
                  <div key={s.label} className="bg-[var(--bg-surface)] p-4 text-center md:text-left">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{s.label}</div>
                    <div className="font-mono text-2xl mt-1">{s.value}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </section>

        {sponsors.length > 0 ? (
          <section className="border-y border-[var(--border-default)] py-8 overflow-hidden">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-center text-[var(--text-tertiary)] mb-4">
              {page.trust?.label || "Partners"}
            </div>
            <div className="flex gap-12 ticker whitespace-nowrap">
              {[...sponsors, ...sponsors].map((s, i) => (
                <span key={`${s}-${i}`} className="font-heading text-xl tracking-[0.2em] text-[var(--border-hover)]">
                  {s}
                </span>
              ))}
            </div>
          </section>
        ) : null}

        <section className="py-16 md:py-24">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-3">{pillars.overline}</div>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium leading-tight">
                {pillars.title}
                <br />
                {pillars.titleLine2}
              </h2>
            </div>
            <div className="lg:col-span-8 grid md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
              {(pillars.items || []).map((p) => (
                <div key={p.title} className="bg-[var(--bg-surface)] p-6 md:p-7">
                  <div className="font-heading text-lg">{p.title}</div>
                  <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="schedule" className="py-16 md:py-24 border-y border-[var(--border-default)] bg-[var(--bg-surface)]">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-3">Schedule</div>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mb-10">Sessions</h2>
            <div className="divide-y divide-[var(--border-default)] border border-[var(--border-default)]">
              {sessions.map((s) => (
                <div key={s.id} className="grid grid-cols-12 gap-4 p-5 md:p-6 items-center hover:bg-[var(--bg-elev)] transition-colors">
                  <div className="col-span-12 md:col-span-2 font-mono text-xs text-[var(--text-secondary)]">{s.time}</div>
                  <div className="col-span-12 md:col-span-7">
                    <div className="font-heading text-lg">{s.title}</div>
                    <div className="text-xs font-mono text-[var(--text-tertiary)] mt-1">{s.room}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3 md:text-right">
                    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[var(--border-default)] text-[var(--text-tertiary)]">
                      {Number(s.capacity) > 0 && Number(s.registered) >= Number(s.capacity)
                        ? "Waitlist"
                        : Number(s.capacity) > 0
                          ? `${Number(s.capacity) - Number(s.registered || 0)} seats left`
                          : "Open"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="speakers" className="py-16 md:py-24">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-3">Speakers</div>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mb-10">Lineup</h2>
            <SpeakersGrid speakers={speakers} accent="var(--primary)" />
          </div>
        </section>

        <section id="venue" className="py-16 md:py-24 border-t border-[var(--border-default)]">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-3">
              Venue · {event.venue?.split("\n")[0]}
            </div>
            <h2 className="font-heading text-[clamp(2rem,8vw,4.5rem)] leading-[1.02] tracking-tight font-semibold">
              {venue.title || "Be in the room."}
            </h2>
            <p className="mt-4 text-[var(--text-secondary)] max-w-xl leading-relaxed">{venue.body}</p>
            <ul className="mt-8 space-y-2 text-sm text-[var(--text-secondary)]">
              {["Demo registration — no payment", "Instant confirmation email", "Badge preview in console"].map((line) => (
                <li key={line} className="flex items-center gap-2">
                  <Check size={14} className="text-[var(--primary)]" />
                  {line}
                </li>
              ))}
            </ul>
            <Link to={registerPath} className="btn-primary mt-10 inline-flex" data-testid="footer-register">
              {cta.label || "Register now"} <ArrowRight size={14} />
            </Link>
          </div>
        </section>

        <footer className="border-t border-[var(--border-default)] py-8">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8 flex flex-wrap justify-between items-center gap-4">
            <EventLogo event={event} />
            <div className="text-[10px] font-mono text-[var(--text-tertiary)]">POWERED BY AIVORYEMS · GLOBAL DEMO</div>
          </div>
        </footer>
      </div>
    </EventDemoScope>
  );
}
